import React from 'react';
import { Box, Typography, Button, Paper, Grid } from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const TabbedFlowEditorPage: React.FC = () => {
  const navigate = useNavigate();

  const handleCreateNew = () => {
    // Navigate to the editor page in the same tab for creating a new workflow
    navigate('/flow-editor/new');
  };

  const handleOpenWorkflows = () => {
    navigate('/dashboard');
  };

  return (
    <Box sx={{ 
      p: 4, 
      height: '100%', 
      bgcolor: '#f8fafc',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <Paper 
        elevation={0}
        sx={{ 
          p: 6, 
          maxWidth: 800, 
          width: '100%',
          textAlign: 'center',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          border: '1px solid #e5e7eb',
          borderRadius: 3
        }}
      >
        <Typography 
          variant="h3" 
          component="h1" 
          gutterBottom
          sx={{ 
            fontWeight: 700,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 3
          }}
        >
          Flow Editor
        </Typography>
        
        <Typography 
          variant="h6" 
          color="text.secondary" 
          sx={{ mb: 4, fontWeight: 400 }}
        >
          Create and edit powerful agent workflows with our visual editor
        </Typography>

        <Box sx={{ display: 'flex', gap: 3, mt: 4, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 4, 
              flex: '1 1 300px',
              maxWidth: 400,
              border: '2px solid #e5e7eb',
              borderRadius: 2,
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              '&:hover': {
                borderColor: '#667eea',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(0,0,0,0.1)'
              }
            }}
            onClick={handleCreateNew}
          >
            <AddIcon sx={{ fontSize: 48, color: '#667eea', mb: 2 }} />
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Create New Workflow
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Start from scratch and build a new workflow with our visual editor
            </Typography>
          </Paper>
          
          <Paper 
            elevation={0}
            sx={{ 
              p: 4, 
              flex: '1 1 300px',
              maxWidth: 400,
              border: '2px solid #e5e7eb',
              borderRadius: 2,
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              '&:hover': {
                borderColor: '#667eea',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(0,0,0,0.1)'
              }
            }}
            onClick={handleOpenWorkflows}
          >
            <EditIcon sx={{ fontSize: 48, color: '#667eea', mb: 2 }} />
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Edit Existing Workflows
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Browse and edit your existing workflows from the workflows page
            </Typography>
          </Paper>
        </Box>

        <Box sx={{ mt: 6, p: 3, bgcolor: '#f1f5f9', borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#374151' }}>
            How it works
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            • Navigate to the Workflows page to see all your existing workflows
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            • Click the "Edit" button on any workflow to open it in a new tab
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • Each workflow opens in its own browser tab for a focused editing experience
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default TabbedFlowEditorPage;